import { useState } from 'react';
import {
  Button,
  Card,
  Badge,
  Input,
  Modal,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { useCreateMembership, useRemoveMembership, useMemberships } from './useIam';
import { formatDate } from '../../shared/utils/format';

/* ── Helpers ── */

/* ── Component ── */

/* `MembershipStatus` do dominio: PENDING, ACTIVE, SUSPENDED, REVOKED. */
const MEMBERSHIP_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendente',
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  REVOKED: 'Revogado',
};

export function MembershipsPage(): JSX.Element {
  const { addToast } = useToast();
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [form, setForm] = useState({ userId: '', roleId: '', tenantId: '' });

  const { data, isLoading, error } = useMemberships();

  const createMutation = useCreateMembership();
  const removeMutation = useRemoveMembership();

  const handleAdd = (): void => {
    if (!form.userId || !form.roleId || !form.tenantId) {
      addToast('Preencha todos os campos', 'warning');
      return;
    }
    createMutation.mutate(form, {
      onSuccess: () => {
        addToast('Membro adicionado com sucesso', 'success');
        setShowAddModal(false);
        setForm({ userId: '', roleId: '', tenantId: '' });
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao adicionar membro', 'error');
      },
    });
  };

  const handleRemove = (membershipId: string): void => {
    removeMutation.mutate(membershipId, {
      onSuccess: () => {
        addToast('Membro removido com sucesso', 'success');
        setConfirmRemoveId(null);
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao remover membro', 'error');
        setConfirmRemoveId(null);
      },
    });
  };

  return (
    <div className="iam-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Membros</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Membros do tenant e seus papéis.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            Adicionar Membro
          </Button>
        </div>
      </div>

      <Card>
        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={6} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">Erro ao carregar membros: {(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="empty-container">
            <p className="empty-message">Nenhum membro encontrado.</p>
            <Button variant="outline" size="sm" onClick={() => setShowAddModal(true)}>
              Adicionar primeiro membro
            </Button>
          </div>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Papéis</th>
                  <th>Situação</th>
                  <th>Tenant</th>
                  <th>Desde</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.map((membership) => (
                  <tr key={membership.id}>
                    <td>
                      {/* `userName` so existe desde que o backend passou a
                          resolve-lo; ate entao esta celula vinha vazia. O id
                          fica de reserva quando o usuario nao e encontrado. */}
                      {membership.userName ?? (
                        <code className="tenant-code">{membership.userId}</code>
                      )}
                    </td>
                    <td>
                      {/* O vinculo tem LISTA de papeis; a tela mostrava um
                          campo `roleName` que nao existia. */}
                      <span className="role-chips">
                        {membership.roles.length === 0 ? (
                          <span className="muted">nenhum</span>
                        ) : (
                          membership.roles.map((role) => (
                            <Badge key={role.id} variant="info" size="sm">
                              {role.name}
                            </Badge>
                          ))
                        )}
                      </span>
                    </td>
                    <td>
                      <Badge variant={membership.status === 'ACTIVE' ? 'success' : 'neutral'} size="sm" dot>
                        {MEMBERSHIP_STATUS_LABEL[membership.status] ?? membership.status}
                      </Badge>
                    </td>
                    <td><code className="tenant-code">{membership.tenantId}</code></td>
                    <td>{formatDate(membership.createdAt)}</td>
                    <td>
                      {confirmRemoveId === membership.id ? (
                        <div className="inline-confirm">
                          <span className="confirm-text">Remover?</span>
                          <Button
                            size="sm"
                            variant="danger"
                            loading={removeMutation.isPending}
                            onClick={() => handleRemove(membership.id)}
                          >
                            Sim
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmRemoveId(null)}
                            disabled={removeMutation.isPending}
                          >
                            Não
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmRemoveId(membership.id)}
                        >
                          Remover
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setForm({ userId: '', roleId: '', tenantId: '' }); }}
        title="Adicionar Membro"
        footer={
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => { setShowAddModal(false); setForm({ userId: '', roleId: '', tenantId: '' }); }}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleAdd}
              disabled={!form.userId || !form.roleId || !form.tenantId || createMutation.isPending}
              loading={createMutation.isPending}
            >
              Adicionar
            </Button>
          </div>
        }
      >
        <div className="modal-form">
          <Input
            label="ID do Usuário"
            value={form.userId}
            onChange={(e) => setForm({ ...form, userId: e.target.value })}
            placeholder="UUID do usuário"
          />
          <Input
            label="ID do Papel"
            value={form.roleId}
            onChange={(e) => setForm({ ...form, roleId: e.target.value })}
            placeholder="UUID do papel"
          />
          <Input
            label="ID do Tenant"
            value={form.tenantId}
            onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
            placeholder="UUID do tenant"
          />
          {createMutation.isError && (
            <p className="field-error">{(createMutation.error as Error).message}</p>
          )}
        </div>
      </Modal>

      <style>{`
        .iam-page { padding: 1.5rem; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
        .empty-message { color: #74777f; font-size: 0.875rem; margin: 0; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        .role-chips { display: inline-flex; flex-wrap: wrap; gap: 4px; }
        .muted { color: #74777f; font-size: 12px; }
        .tenant-code { font-size: 0.75rem; background: #f2f4f6; padding: 0.125rem 0.375rem; border-radius: 4px; }
        .inline-confirm { display: flex; align-items: center; gap: 0.375rem; }
        .confirm-text { font-size: 0.8125rem; color: #ba1a1a; font-weight: 600; margin-right: 0.25rem; }
        .modal-form { display: flex; flex-direction: column; gap: 1rem; }
        .modal-actions { display: flex; gap: 0.5rem; }
        .field-error { color: #ba1a1a; font-size: 0.8125rem; margin: 0; }
      `}</style>
    </div>
  );
}
